import "./TickerContent.sass";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import TickerItem from "./TickerItem";
import TickerSportContent from "./sports/TickerSportContent";
import { MENU_DURATION, SCROLL_SPEED, FOOT_RESULT_DURATION, NEWS_ROTATION_INTERVAL, SHOW_UPCOMING, SHOW_RESULTS, SHOW_NEWS, SHOW_BOTH_BLOCKS } from "../constants-ticker";
import { shouldShowUpcoming, toArray } from "./sports/tickerSportUtils";

// Duración de la animación de entrada de cada slide vertical. Debe coincidir con
// el `transition.duration` de TickerResultsContent y TickerNewsContent para que
// el `holdMs` de la parada cubra el tiempo real de rotación.
const RESULT_SLIDE_ENTER_MS = 800;
const NEWS_SLIDE_ENTER_MS = 450;

const hasEvents = (events) => Array.isArray(events)
  ? events.length > 0
  : Boolean(events && typeof events === "object" && Object.keys(events).length);

const countEvents = (events) => Array.isArray(events)
  ? events.filter(Boolean).length
  : events && typeof events === "object"
    ? Object.values(events).filter(Boolean).length
    : 0;

/**
 * Alterna entre el menú ordenado de ligas y el contenido de la primera liga.
 *
 * El scroll horizontal puede tener "paradas": posiciones donde el desplazamiento
 * se detiene un tiempo para que un bloque rote verticalmente en su lugar (por
 * ejemplo, el carrusel de resultados FOOT o el de noticias). Al terminar cada
 * parada, el scroll continúa hasta la siguiente y, finalmente, hasta el final.
 */
export default function TickerContent({ tickerData = [] }) {
  const [leagueQueue, setLeagueQueue] = useState([]);
  const [phase, setPhase] = useState("menu");
  const [contentWidth, setContentWidth] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(0);
  // Ancho total de la ventana: se usa solo para que el bloque entre desde el
  // borde derecho de la página y su transición se aprecie por completo.
  const [pageWidth, setPageWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 0);
  // Índice del objetivo (target) actual del recorrido. Los objetivos son, en
  // orden: cada parada (results/news) por su offset y, al final, el borde derecho
  // (maxScrollX). El scroll SIEMPRE viaja hacia el objetivo actual; nunca se queda
  // detenido esperando mediciones (evita el standing congelado).
  const [targetIndex, setTargetIndex] = useState(0);
  // `true` cuando el scroll ya llegó (onAnimationComplete) al objetivo actual y
  // está "posado" (haciendo el hold vertical de la parada). `false` mientras viaja.
  const [holding, setHolding] = useState(false);
  const [footResultsOffset, setFootResultsOffset] = useState(null);
  const [newsOffset, setNewsOffset] = useState(null);
  const wrapperRef = useRef(null);
  const contentRef = useRef(null);
  const resultsBlockRef = useRef(null);
  const newsBlockRef = useRef(null);
  const sourceLeaguesRef = useRef([]);

  const SUPPORTED_SPORTS = ["FOOT", "BASE", "AMFO"];
  const sourceLeagues = Array.isArray(tickerData)
    ? tickerData.filter((entry) => {
        const isSupported = SUPPORTED_SPORTS.includes(entry?.league?.sport_code?.toUpperCase());
        // Solo se incluye la liga si trae contenido en al menos uno de sus
        // bloques (results, upcoming o news). Si los tres vienen vacíos, no se
        // agrega al menú de ligas.
        const hasContent = hasEvents(entry?.results) || hasEvents(entry?.upcoming) || hasEvents(entry?.news);
        return isSupported && hasContent;
      })
    : [];
  const sourceLeaguesKey = sourceLeagues
    .map((entry, index) => entry.league.id ?? entry.league.name ?? index)
    .join("|");

  sourceLeaguesRef.current = sourceLeagues;

  useEffect(() => {
    setLeagueQueue(sourceLeaguesRef.current);
    setContentWidth(0);
    setTargetIndex(0);
    setHolding(false);
    setFootResultsOffset(null);
    setNewsOffset(null);
    setPhase("menu");
  }, [sourceLeaguesKey]);

  const currentLeagueData = leagueQueue[0];
  const currentLeagueId = currentLeagueData?.league?.id || "empty";
  const currentSportCode = currentLeagueData?.league?.sport_code?.toUpperCase();
  const isBase = currentSportCode === "BASE";
  // BASE (MLB) y AMFO (NFL) muestran ambos bloques (results + upcoming) solo si
  // `SHOW_BOTH_BLOCKS` está activo; si no, aplican la validación de FOOT.
  const showsBothBlocks = (isBase || currentSportCode === "AMFO") && SHOW_BOTH_BLOCKS;
  // A 24h o menos del primer upcoming se ocultan los resultados (validación de
  // FOOT). Cuando `showsBothBlocks` es true (BASE/AMFO con la bandera activa) se
  // muestran SIEMPRE ambos bloques: los results con carrusel vertical (parada) y
  // el upcoming como listado horizontal, por lo que no alternan con `showUpcoming`.
  const showUpcoming = !showsBothBlocks
    && SHOW_UPCOMING
    && shouldShowUpcoming(currentLeagueData?.upcoming);
  const footResultsCount = (showUpcoming || !SHOW_RESULTS) ? 0 : countEvents(currentLeagueData?.results);
  // FOOT, BASE (MLB) y AMFO usan el carrusel vertical de resultados, por lo que
  // requieren una parada del scroll para rotar sus tarjetas en su lugar.
  const usesVerticalResults = currentSportCode === "FOOT" || isBase || currentSportCode === "AMFO";
  const hasFootResults = usesVerticalResults && footResultsCount > 0;
  const newsCount = SHOW_NEWS ? toArray(currentLeagueData?.news).filter(Boolean).length : 0;
  const hasNews = newsCount > 0;

  const hasResultCards = showUpcoming
    ? hasEvents(currentLeagueData?.upcoming)
    : SHOW_RESULTS && hasEvents(currentLeagueData?.results);
  const heightReservationClass = hasResultCards
    ? (currentSportCode === "FOOT" || isBase || currentSportCode === "AMFO")
      ? "ticker-content-section--reserve-foot-height"
      : "ticker-content-section--reserve-result-height"
    : "";

  // El carril entra desde el borde derecho (x = pageWidth) y se desplaza a la
  // izquierda hasta x = -fullTravelX. Para que TODO el contenido cruce el viewport
  // —incluido el contenido más angosto que el viewport— el destino final debe
  // desplazar el carril su ancho COMPLETO (`contentWidth`): así su borde derecho
  // alcanza el borde izquierdo del wrapper y no queda pegado a la izquierda.
  //
  // Antes se usaba `maxScrollX = max(contentWidth - viewport, 0)`, que se
  // saturaba en 0 para contenido angosto (destino x≈0 → cards pegadas a la
  // izquierda). `fullTravelX = contentWidth` garantiza el recorrido completo.
  const fullTravelX = Math.max(contentWidth, 0);

  const isMenuVisible = phase === "menu";

  // Paradas ordenadas por offset. Cada parada detiene el scroll `holdMs` para que
  // su bloque rote verticalmente. Solo se incluyen las que YA tienen offset medido;
  // las no medidas simplemente no generan parada (el scroll no se bloquea por ellas).
  const stops = useMemo(() => {
    const list = [];
    if (hasFootResults && footResultsOffset != null) {
      list.push({
        kind: "foot-results",
        offset: footResultsOffset,
        holdMs: footResultsCount * (RESULT_SLIDE_ENTER_MS + FOOT_RESULT_DURATION),
      });
    }
    if (hasNews && newsOffset != null) {
      list.push({
        kind: "news",
        offset: newsOffset,
        holdMs: newsCount * (NEWS_SLIDE_ENTER_MS + NEWS_ROTATION_INTERVAL),
      });
    }
    return list.sort((a, b) => a.offset - b.offset);
  }, [hasFootResults, footResultsOffset, footResultsCount, hasNews, newsOffset, newsCount]);

  // Lista de objetivos del recorrido: cada parada + el borde final (fullTravelX).
  // El scroll viaja secuencialmente por estos objetivos. El último SIEMPRE es el
  // borde final (ancho completo del carril), de modo que el recorrido en X se
  // completa por entero y todo el contenido cruza el viewport.
  const targets = useMemo(() => {
    const list = stops.map((stop) => ({
      kind: stop.kind,
      x: Math.min(stop.offset, fullTravelX),
      holdMs: stop.holdMs,
    }));
    list.push({ kind: "end", x: fullTravelX, holdMs: 0 });
    return list;
  }, [stops, fullTravelX]);

  // Índice seguro dentro de `targets` (puede quedar transitoriamente fuera de
  // rango si `targets` se recalcula antes de que un efecto ajuste el índice).
  const safeTargetIndex = Math.min(targetIndex, targets.length - 1);
  const currentTarget = targets[safeTargetIndex] ?? targets[targets.length - 1];
  const previousTargetX = safeTargetIndex > 0 ? (targets[safeTargetIndex - 1]?.x ?? 0) : 0;
  const isFinalTarget = safeTargetIndex >= targets.length - 1;
  // Valores PRIMITIVOS del objetivo actual. Se usan en efectos/props en lugar del
  // objeto `currentTarget` (que se recrea en cada render cuando el contenido se
  // remide), para que NO fluctúen mientras el carrusel rota en su parada. Sin
  // esto, `newsActive` parpadeaba y `TickerNewsContent` reiniciaba su índice a 0
  // (news congelado en el primer elemento).
  const currentTargetKind = currentTarget?.kind ?? "end";
  const currentTargetHoldMs = currentTarget?.holdMs ?? 0;
  const currentStopKind = holding && !isFinalTarget ? currentTargetKind : null;

  // Posición X objetivo del carril (translateX negativo) y duración del tramo.
  //
  // La duración debe ser proporcional a la distancia REAL animada para mantener
  // una velocidad constante de SCROLL_SPEED px/s. En el PRIMER tramo el carril
  // parte de x = +pageWidth y llega a x = -targetX, recorriendo `pageWidth +
  // targetX` px (no solo `targetX`). En los tramos siguientes el carril ya está
  // en x = -previousTargetX y viaja hasta x = -targetX, recorriendo
  // `targetX - previousTargetX` px.
  const targetX = currentTarget?.x ?? fullTravelX;
  const isFirstTarget = safeTargetIndex === 0;
  const entryStartX = pageWidth || viewportWidth;
  const travelDistance = isFirstTarget
    ? Math.max(entryStartX + targetX, 0)
    : Math.max(targetX - previousTargetX, 0);
  const travelDuration = holding ? 0 : Math.max(travelDistance / SCROLL_SPEED, 0.1);

  useEffect(() => {
    setTargetIndex(0);
    setHolding(false);
    setFootResultsOffset(null);
    setNewsOffset(null);
  }, [currentLeagueId]);

  // Muestra el listado general durante dos segundos antes del scroll hijo.
  useEffect(() => {
    if (phase !== "menu" || !currentLeagueData) return undefined;

    const timer = window.setTimeout(() => {
      setContentWidth(0);
      setTargetIndex(0);
      setHolding(false);
      setFootResultsOffset(null);
      setNewsOffset(null);
      setPhase("content");
    }, MENU_DURATION);

    return () => window.clearTimeout(timer);
  }, [currentLeagueData, phase]);

  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;

    if (!wrapper) return undefined;

    const updateViewportWidth = () => {
      setViewportWidth(wrapper.clientWidth);
      setPageWidth(window.innerWidth);
    };
    updateViewportWidth();

    window.addEventListener("resize", updateViewportWidth);

    if (typeof ResizeObserver === "undefined") {
      return () => window.removeEventListener("resize", updateViewportWidth);
    }

    const resizeObserver = new ResizeObserver(updateViewportWidth);
    resizeObserver.observe(wrapper);
    return () => {
      window.removeEventListener("resize", updateViewportWidth);
      resizeObserver.disconnect();
    };
  }, []);

  useLayoutEffect(() => {
    const content = contentRef.current;

    if (phase !== "content" || !content) return undefined;

    const updateContentWidth = () => setContentWidth(content.getBoundingClientRect().width);
    updateContentWidth();

    if (typeof ResizeObserver === "undefined") return undefined;

    const resizeObserver = new ResizeObserver(updateContentWidth);
    resizeObserver.observe(content);

    return () => resizeObserver.disconnect();
  }, [currentLeagueId, phase]);

  // Mide el offset horizontal de los bloques que actúan como parada.
  useLayoutEffect(() => {
    const content = contentRef.current;

    if (phase !== "content" || !content) return undefined;

    const updateOffsets = () => {
      if (hasFootResults && resultsBlockRef.current) {
        setFootResultsOffset(resultsBlockRef.current.offsetLeft);
      }
      if (hasNews && newsBlockRef.current) {
        setNewsOffset(newsBlockRef.current.offsetLeft);
      }
    };
    updateOffsets();

    if (typeof ResizeObserver === "undefined") return undefined;

    const resizeObserver = new ResizeObserver(updateOffsets);
    resizeObserver.observe(content);
    if (resultsBlockRef.current) resizeObserver.observe(resultsBlockRef.current);
    if (newsBlockRef.current) resizeObserver.observe(newsBlockRef.current);
    return () => resizeObserver.disconnect();
  }, [currentLeagueId, hasFootResults, hasNews, phase]);

  // Refs para que el "director" del recorrido lea los valores vigentes del tramo
  // actual sin re-ejecutarse (y reiniciar sus timers) cuando el contenido se
  // remide durante el hold (p. ej. el carrusel al rotar en Y).
  const travelDurationRef = useRef(0.1);
  const currentTargetHoldMsRef = useRef(0);
  const isFinalTargetRef = useRef(true);
  const rotateLeagueRef = useRef(() => {});

  travelDurationRef.current = travelDuration;
  currentTargetHoldMsRef.current = currentTargetHoldMs;
  isFinalTargetRef.current = isFinalTarget;

  rotateLeagueRef.current = () => {
    setLeagueQueue((currentQueue) => (
      currentQueue.length > 1
        ? [...currentQueue.slice(1), currentQueue[0]]
        : currentQueue
    ));
    setPhase("menu");
  };

  const rotateLeague = () => rotateLeagueRef.current();

  // El recorrido solo debe arrancar cuando las mediciones necesarias están
  // listas: el ancho del carril (`contentWidth > 0`) y los offsets de TODAS las
  // paradas esperadas (results/news). Si arranca antes, `targets` solo contiene
  // el borde final (offsets aún null) y el director rotaría de liga saltándose
  // las paradas (p. ej. no se vería el bloque de noticias).
  const measurementsReady = contentWidth > 0
    && (!hasFootResults || footResultsOffset != null)
    && (!hasNews || newsOffset != null);

  // DIRECTOR del recorrido con timers deterministas. Se ejecuta una vez por cada
  // `targetIndex` (y por liga/fase). Secuencia por tramo:
  //  1) Viaje: espera `travelDuration` (la animación X del carril corre en
  //     paralelo con la MISMA duración) y marca `holding` (posado).
  //  2) Pausa: si es una parada, espera su `holdMs` (rotación vertical del
  //     bloque) y avanza al siguiente objetivo; si es el borde final, espera
  //     `MENU_DURATION` y rota a la siguiente liga.
  //
  // No depende de `onAnimationComplete` (evita quedarse detenido cuando un tramo
  // tiene distancia ~0 y Framer no dispara el callback). Lee tiempos desde refs
  // para no reiniciarse por remediciones durante el hold.
  useEffect(() => {
    // No arrancar hasta que el contenido y las paradas estén medidos: así
    // `targets` ya incluye las paradas reales (results/news) y el recorrido no
    // se las salta.
    if (phase !== "content" || !measurementsReady) return undefined;

    setHolding(false);

    let holdTimer;
    const travelTimer = window.setTimeout(() => {
      setHolding(true);
      const holdMs = isFinalTargetRef.current ? MENU_DURATION : currentTargetHoldMsRef.current;
      holdTimer = window.setTimeout(() => {
        if (isFinalTargetRef.current) {
          rotateLeagueRef.current();
        } else {
          setTargetIndex((index) => index + 1);
        }
      }, holdMs);
    }, Math.max(travelDurationRef.current * 1000, 100));

    return () => {
      window.clearTimeout(travelTimer);
      window.clearTimeout(holdTimer);
    };
  }, [phase, currentLeagueId, targetIndex, measurementsReady]);

  return (
    <div className={`ticker-content-section ${heightReservationClass}`}>
      {phase === "content" && currentLeagueData?.league && (
        <motion.div
          key={`fixed-league-${currentLeagueId}`}
          className="ticker-content-fixed-league"
          initial={{ opacity: 0, x: 0 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
        >
          {holding && currentStopKind === "news" && <span className="item-news"> Noticias</span>}
          <TickerItem item={{ text: currentLeagueData.league.name || "Liga" }} />
        </motion.div>
      )}
      <div ref={wrapperRef} className="ticker-content-wrapper">
        {!leagueQueue.length ? (
          <div className="ticker-content-empty" role="status" aria-live="polite">
            {/* <span className="ticker-content-empty__dot" aria-hidden="true" /> */}
            <span className="ticker-content-empty__text">
                {/* No hay información deportiva disponible por el momento */}
            </span>
          </div>
        ) : (
          <>
            <AnimatePresence>
              {isMenuVisible && (
                <motion.div
                  key={`menu-${currentLeagueId}`}
                  className="ticker-content-menu"
                  initial={{ opacity: 0, scaleX: 0 }}
                  animate={{ opacity: 1, scaleX: 1 }}
                  exit={{ opacity: 0, x: "-100%", transition: { duration: 2, ease: [0.4, 0, 0.2, 1] } }}
                  transition={{ duration: 0.8, ease: [0.8, 0, 0.2, 1] }}
                >
                  {leagueQueue.map((leagueData) => (
                    <div
                      key={leagueData.league.id}
                      className="ticker-content-menu__item"
                    >
                      {leagueData.league.name || "Liga"}
                    </div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
            {!isMenuVisible && (
              <motion.div
                key={`content-fade-${currentLeagueId}`}
                className="ticker-content-fade"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1.8, ease: "easeInOut" }}
              >
                <motion.div
                  ref={contentRef}
                  className="ticker-content-scroll ticker-content-scroll--league"
                  initial={{ x: pageWidth || viewportWidth }}
                  animate={{ x: -targetX }}
                  transition={{ duration: travelDuration, ease: "linear" }}
                >
                  <TickerSportContent
                    leagueData={currentLeagueData}
                    showLeague={false}
                    resultsRef={resultsBlockRef}
                    newsRef={newsBlockRef}
                    footResultsActive={holding && currentStopKind === "foot-results"}
                    newsActive={holding && currentStopKind === "news"}
                  />
                </motion.div>
              </motion.div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
